(() => {
  if (customElements.get("variant-product-grid")) return;

  const variantWishlist = {
    refresh(root = document) {
      const scope = root instanceof Element || root instanceof Document ? root : document;
      const cards = scope.matches?.("[data-wishlist-variant-id]")
        ? [scope]
        : Array.from(scope.querySelectorAll?.("[data-wishlist-variant-id]") || []);

      cards.forEach((card) => this.prepareCard(card));
    },

    prepareCard(card) {
      const variantId = card.dataset.wishlistVariantId;
      const productHandle = card.dataset.wishlistProductHandle || card.dataset.product;
      const productId = card.dataset.wishlistProductId || card.dataset.productId;
      const productUrl = card.dataset.wishlistProductUrl;
      const variantTitle = card.dataset.wishlistVariantTitle;

      if (!variantId || !productHandle || !productUrl) return;

      this.prepareProductLinks(card, { variantId, productHandle, productId, productUrl });
      this.prepareWishlistButtons(card, {
        variantId,
        productHandle,
        productId,
        productUrl,
        variantTitle,
      });
    },

    prepareProductLinks(card, data) {
      card.querySelectorAll("[data-wishlist-product-link], .card-product__link").forEach((link) => {
        this.applyIdentity(link, data);
        if (link.tagName === "A") link.setAttribute("href", data.productUrl);
      });
    },

    prepareWishlistButtons(card, data) {
      const wishlistRoots = card.querySelectorAll(
        "wishlist-button-collection, wishlist-button-block"
      );

      wishlistRoots.forEach((root) => {
        this.applyIdentity(root, data);
        root.querySelectorAll("button, a, .wk-button").forEach((button) => {
          this.applyIdentity(button, data);
          if (button.tagName === "A") button.setAttribute("href", data.productUrl);
          if (data.variantTitle && !button.getAttribute("aria-label")) {
            button.setAttribute("aria-label", data.variantTitle);
          }
        });
      });
    },

    applyIdentity(element, data) {
      element.setAttribute("data-product-handle", data.productHandle);
      element.setAttribute("data-wk-product-handle", data.productHandle);
      element.setAttribute("data-handle", data.productHandle);
      element.setAttribute("data-product-url", data.productUrl);
      element.setAttribute("data-wk-product-url", data.productUrl);
      element.setAttribute("data-url", data.productUrl);
      element.setAttribute("data-variant-url", data.productUrl);
      element.setAttribute("data-variant-id", data.variantId);
      element.setAttribute("data-wk-variant-id", data.variantId);
      element.setAttribute("data-product-variant-id", data.variantId);

      if (data.productId) {
        element.setAttribute("data-product-id", data.productId);
        element.setAttribute("data-wk-product-id", data.productId);
      }
    },

    observe() {
      if (this.observer || !document.body) return;

      this.refresh();

      document.addEventListener(
        "click",
        (event) => {
          const target = event.target instanceof Element ? event.target : event.target?.parentElement;
          const button = target?.closest(
            "wishlist-button-collection, wishlist-button-block, .wk-button"
          );
          const card = button?.closest("[data-wishlist-variant-id]");
          if (card) this.prepareCard(card);
        },
        true
      );

      this.observer = new MutationObserver((mutations) => {
        const needsRefresh = mutations.some((mutation) =>
          Array.from(mutation.addedNodes).some((node) => {
            if (!(node instanceof Element)) return false;
            return (
              node.matches("[data-wishlist-variant-id], wishlist-button-collection, wishlist-button-block, .wk-button") ||
              node.querySelector("[data-wishlist-variant-id], wishlist-button-collection, wishlist-button-block, .wk-button")
            );
          })
        );

        if (needsRefresh) {
          window.requestAnimationFrame(() => this.refresh());
        }
      });

      this.observer.observe(document.body, { childList: true, subtree: true });
    },
  };

  window.variantProductWishlist = variantWishlist;

  class VariantProductGrid extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.cards = [];
      this.onPaginationClick = this.onPaginationClick.bind(this);
      this.onHistoryChange = this.onHistoryChange.bind(this);
    }

    connectedCallback() {
      this.abortController = new AbortController();
      this.addEventListener("click", this.onPaginationClick);
      window.addEventListener("popstate", this.onHistoryChange);
      this.initialize();
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.onPaginationClick);
      window.removeEventListener("popstate", this.onHistoryChange);
      this.abortController?.abort();
    }

    async initialize() {
      try {
        await this.loadAllParentPages();
        if (!this.isConnected || this.abortController.signal.aborted) return;

        this.cards = Array.from(this.querySelectorAll("[data-variant-card]"));
        window.variantProductWishlist?.refresh(this);
        this.render();
        this.setAttribute("aria-busy", "false");
        this.dataset.variantReady = "true";
      } catch (error) {
        if (error.name === "AbortError") return;

        console.warn("[Variant grid] Additional collection pages could not be loaded.", error);
        this.cards = Array.from(this.querySelectorAll("[data-variant-card]"));
        window.variantProductWishlist?.refresh(this);
        this.cards.forEach((card) => (card.hidden = false));
        this.updateResultCount(this.cards.length);
        this.classList.add("variant-product-grid--error");
        this.setAttribute("aria-busy", "false");
      }
    }

    async loadAllParentPages() {
      const totalPages = Number.parseInt(this.dataset.parentPages || "1", 10);
      const currentPage = Number.parseInt(this.dataset.parentCurrentPage || "1", 10);
      const currentList = this.querySelector("[data-variant-product-list]");

      if (!currentList || totalPages <= 1) return;

      const pageLists = new Array(totalPages);
      pageLists[currentPage - 1] = Array.from(currentList.children);

      const pageRequests = [];
      for (let page = 1; page <= totalPages; page += 1) {
        if (page === currentPage) continue;
        pageRequests.push(this.fetchParentPage(page));
      }

      const responses = await Promise.all(pageRequests);
      responses.forEach(({ page, cards }) => {
        pageLists[page - 1] = cards;
      });

      const orderedCards = pageLists.flatMap((cards) => cards || []);
      currentList.replaceChildren(...orderedCards);
    }

    async fetchParentPage(page) {
      const url = new URL(window.location.href);
      url.searchParams.delete(this.dataset.pageParam || "variant_page");
      url.searchParams.set("page", String(page));
      url.searchParams.set("section_id", this.dataset.sectionId);

      const response = await fetch(url.toString(), {
        signal: this.abortController.signal,
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });

      if (!response.ok) {
        throw new Error(`Collection page ${page} returned ${response.status}`);
      }

      const html = await response.text();
      const parsedDocument = new DOMParser().parseFromString(html, "text/html");
      const list = parsedDocument.querySelector("[data-variant-product-list]");

      if (!list) throw new Error(`Collection page ${page} has no variant list`);

      return { page, cards: Array.from(list.children) };
    }

    getActiveVariantFilters() {
      const params = new URLSearchParams(window.location.search);
      return {
        availability: params.getAll("filter.v.availability"),
        dimensions: params.getAll("filter.v.option.dimensions"),
        price: params.getAll("filter.v.m.custom.price_per_square_foot"),
      };
    }

    cardMatches(card, filters) {
      if (
        filters.availability.length > 0 &&
        !filters.availability.includes(card.dataset.variantAvailable)
      ) {
        return false;
      }

      if (
        filters.dimensions.length > 0 &&
        !filters.dimensions.includes(card.dataset.variantDimensions || "")
      ) {
        return false;
      }

      if (filters.price.length > 0 && !this.matchesPriceFilter(card, filters.price)) {
        return false;
      }

      return true;
    }

    matchesPriceFilter(card, selectedValues) {
      const rawPrice = card.dataset.variantPricePerSquareFoot;
      const numericPrice = this.toNumber(rawPrice);

      if (rawPrice == null || rawPrice === "") return false;

      return selectedValues.some((selectedValue) => {
        if (selectedValue.startsWith("gid://shopify/FilterSettingGroup/")) {
          const range = this.getPriceGroupRange(selectedValue);
          return (
            range != null &&
            numericPrice != null &&
            numericPrice >= range.minimum &&
            numericPrice <= range.maximum
          );
        }

        const selectedNumber = this.toNumber(selectedValue);
        if (selectedNumber != null && numericPrice != null) {
          return Math.abs(selectedNumber - numericPrice) < 0.0001;
        }

        return selectedValue === rawPrice;
      });
    }

    getPriceGroupRange(groupValue) {
      const inputs = Array.from(
        document.querySelectorAll(
          'input[name="filter.v.m.custom.price_per_square_foot"]'
        )
      );
      const input = inputs.find((candidate) => candidate.value === groupValue);
      const listItem = input?.closest("li");
      const desktopLabel = listItem?.querySelector(".facet-checkbox__label");
      const label = desktopLabel?.textContent || listItem?.querySelector("label")?.textContent;

      if (!label) return null;

      const matches = Array.from(label.matchAll(/\d+(?:[.,]\d+)?/g));
      if (matches.length < 2) return null;

      const minimum = this.toNumber(matches[0][0]);
      const maximum = this.toNumber(matches[1][0]);
      return minimum == null || maximum == null ? null : { minimum, maximum };
    }

    toNumber(value) {
      if (value == null || value === "") return null;
      const number = Number.parseFloat(String(value).replace(",", "."));
      return Number.isFinite(number) ? number : null;
    }

    render() {
      const filters = this.getActiveVariantFilters();
      const matchingCards = this.cards.filter((card) => this.cardMatches(card, filters));
      const pageSize = Math.max(1, Number.parseInt(this.dataset.pageSize || "24", 10));
      const totalPages = Math.max(1, Math.ceil(matchingCards.length / pageSize));
      const requestedPage = this.getRequestedPage();
      const currentPage = Math.min(requestedPage, totalPages);
      const startIndex = (currentPage - 1) * pageSize;
      const visibleCards = new Set(matchingCards.slice(startIndex, startIndex + pageSize));

      this.cards.forEach((card) => {
        card.hidden = !visibleCards.has(card);
      });

      const emptyState = this.querySelector("[data-variant-empty]");
      if (emptyState) emptyState.hidden = matchingCards.length !== 0;

      this.updateResultCount(matchingCards.length);
      this.renderPagination(currentPage, totalPages, matchingCards.length);
      window.variantProductWishlist?.refresh(this);

      if (currentPage !== requestedPage) {
        this.updatePageUrl(currentPage, true);
      }
    }

    getRequestedPage() {
      const paramName = this.dataset.pageParam || "variant_page";
      const page = Number.parseInt(new URLSearchParams(window.location.search).get(paramName) || "1", 10);
      return Number.isFinite(page) && page > 0 ? page : 1;
    }

    updateResultCount(count) {
      const label = count === 1 ? this.dataset.countSingular : this.dataset.countPlural;
      const countText = `${count} ${label}`;
      const targets = [
        document.getElementById("ProductCountDesktop"),
        document.getElementById("ProductCount"),
        document.querySelector(".mobile-facets__count"),
      ];

      targets.forEach((target) => {
        if (!target) return;
        target.textContent = countText;
        target.classList.remove("loading");
      });
    }

    renderPagination(currentPage, totalPages, resultCount) {
      const container = this.querySelector("[data-variant-pagination]");
      if (!container) return;

      if (totalPages <= 1 || resultCount === 0) {
        container.replaceChildren();
        return;
      }

      const isFrench = document.documentElement.lang.toLowerCase().startsWith("fr");
      const navigationLabel = isFrench ? "Pagination des options" : "Option pagination";
      const previousLabel = isFrench ? "Page précédente" : "Previous page";
      const nextLabel = isFrench ? "Page suivante" : "Next page";
      const pageLabel = (page) => (isFrench ? `Page ${page}` : `Page ${page}`);
      const wrapper = document.createElement("div");
      wrapper.className = "variant-pagination-wrapper";
      const navigation = document.createElement("nav");
      navigation.className = "variant-pagination";
      navigation.setAttribute("role", "navigation");
      navigation.setAttribute("aria-label", navigationLabel);
      const list = document.createElement("ul");
      list.className = "variant-pagination__list list-unstyled";

      if (currentPage > 1) {
        list.appendChild(this.createPageItem(currentPage - 1, "‹", previousLabel, false, "previous"));
      }

      this.getPaginationParts(currentPage, totalPages).forEach((page) => {
        if (page === "ellipsis") {
          const item = document.createElement("li");
          item.innerHTML = '<span class="variant-pagination__item" aria-hidden="true">…</span>';
          list.appendChild(item);
          return;
        }

        list.appendChild(
          this.createPageItem(page, String(page), pageLabel(page), page === currentPage)
        );
      });

      if (currentPage < totalPages) {
        list.appendChild(this.createPageItem(currentPage + 1, "›", nextLabel, false, "next"));
      }

      navigation.appendChild(list);
      wrapper.appendChild(navigation);
      container.replaceChildren(wrapper);
    }

    getPaginationParts(currentPage, totalPages) {
      if (totalPages <= 9) {
        return Array.from({ length: totalPages }, (_, index) => index + 1);
      }

      const pages = new Set([1, totalPages]);
      for (let page = currentPage - 2; page <= currentPage + 2; page += 1) {
        if (page > 1 && page < totalPages) pages.add(page);
      }

      const sortedPages = Array.from(pages).sort((a, b) => a - b);
      const parts = [];
      sortedPages.forEach((page, index) => {
        if (index > 0 && page - sortedPages[index - 1] > 1) parts.push("ellipsis");
        parts.push(page);
      });
      return parts;
    }

    createPageItem(page, text, ariaLabel, current, direction = "") {
      const item = document.createElement("li");
      if (current) {
        const currentElement = document.createElement("span");
        currentElement.className = "variant-pagination__item variant-pagination__item--current";
        currentElement.setAttribute("aria-current", "page");
        currentElement.setAttribute("aria-label", ariaLabel);
        currentElement.textContent = text;
        item.appendChild(currentElement);
        return item;
      }

      const button = document.createElement("button");
      button.type = "button";
      button.className = `variant-pagination__item variant-pagination__item--button${
        direction ? ` variant-pagination__item--${direction}` : ""
      }`;
      button.dataset.variantPage = String(page);
      button.setAttribute("aria-label", ariaLabel);
      button.textContent = text;
      item.appendChild(button);
      return item;
    }

    onPaginationClick(event) {
      const button = event.target.closest("[data-variant-page]");
      if (!button || !this.contains(button)) return;

      event.preventDefault();
      const page = Number.parseInt(button.dataset.variantPage, 10);
      if (!Number.isFinite(page) || page < 1) return;

      this.updatePageUrl(page, false);
      this.render();

      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      this.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
    }

    onHistoryChange() {
      if (this.cards.length > 0) this.render();
    }

    updatePageUrl(page, replace) {
      const url = new URL(window.location.href);
      const paramName = this.dataset.pageParam || "variant_page";
      url.searchParams.delete("page");
      if (page <= 1) url.searchParams.delete(paramName);
      else url.searchParams.set(paramName, String(page));

      const state = { searchParams: url.search.slice(1) };
      if (replace) history.replaceState(state, "", url.toString());
      else history.pushState(state, "", url.toString());
    }
  }

  customElements.define("variant-product-grid", VariantProductGrid);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => variantWishlist.observe(), { once: true });
  } else {
    variantWishlist.observe();
  }
})();